/**
 * usePlanOverrides (W25 lean override slice).
 *
 * Loads a plan's ACTIVE overrides and exposes a constraint_id → override map for
 * the audit's dual-status overlay, plus create / revoke actions (each refreshes).
 * The override's `constraint_id` is the audit requirement's `requirement_code`,
 * so the overlay matches an override to its requirement.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Override,
  OverrideDraft,
  overrideService,
} from '../services/overrideService';

export interface UsePlanOverridesResult {
  activeOverrides: Override[];
  byConstraint: Map<string, Override>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createOverride: (draft: OverrideDraft) => Promise<Override>;
  revokeOverride: (overrideId: number, reason: string) => Promise<void>;
}

export function usePlanOverrides(
  planId: number | null,
  onMutate?: () => void
): UsePlanOverridesResult {
  const [activeOverrides, setActiveOverrides] = useState<Override[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!planId) {
      setActiveOverrides([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setActiveOverrides(await overrideService.list(planId, { status: 'active' }));
    } catch {
      setError('Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createOverride = useCallback(
    async (draft: OverrideDraft) => {
      if (!planId) throw new Error('No plan selected');
      const created = await overrideService.create(planId, draft);
      await refresh();
      // W26: refetch the degree audit so the requirement's effective status updates.
      onMutate?.();
      return created;
    },
    [planId, refresh, onMutate]
  );

  const revokeOverride = useCallback(
    async (overrideId: number, reason: string) => {
      if (!planId) return;
      await overrideService.revoke(planId, overrideId, reason);
      await refresh();
      onMutate?.();
    },
    [planId, refresh, onMutate]
  );

  const byConstraint = useMemo(() => {
    const map = new Map<string, Override>();
    for (const o of activeOverrides) {
      if (!map.has(o.constraint_id)) map.set(o.constraint_id, o);
    }
    return map;
  }, [activeOverrides]);

  return {
    activeOverrides,
    byConstraint,
    loading,
    error,
    refresh,
    createOverride,
    revokeOverride,
  };
}
